import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Badge, Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Snackbar, Typography, Divider } from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusinessOutlined';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import EventIcon from '@mui/icons-material/EventOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import HomeWorkIcon from '@mui/icons-material/HomeWorkOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import BarChartIcon from '@mui/icons-material/BarChartOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import WebIcon from '@mui/icons-material/WebOutlined';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import ChatIcon from '@mui/icons-material/ChatOutlined';
import GavelIcon from '@mui/icons-material/GavelOutlined';
import PaymentIcon from '@mui/icons-material/PaymentOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import CastForEducationIcon from '@mui/icons-material/CastForEducationOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import LocalOfferIcon from '@mui/icons-material/LocalOfferOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptLongOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import { useTheme } from '@mui/material/styles';

import { useAuth } from '../../auth/contexts/AuthenticationContext';
import { useIsSystemAdmin, useProfile } from '../../auth/hooks';
import { displayNameFor } from '../../auth/helpers';
import { useActiveContext, PlatformView } from '../../auth/contexts/ActiveOrgContext';
import AccountSwitcher from '../../auth/components/AccountSwitcher';
import RequestNewOrgDialog from '../../organization/components/RequestNewOrgDialog';
import { useBilling } from '../../billing/hooks';
import { STRIPE_BRAND } from '../../../shared/constants/colors';

const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** When > 0, renders a small badge next to the label. Use for at-a-glance
   *  counters like "unpaid invoices" or "pending approvals". Hidden when
   *  zero or undefined so the nav stays clean for users with nothing owed. */
  badgeCount?: number;
}

interface ExternalLink {
  label: string;
  url: string;
  icon: string;
}

// Static: every platform admin sees the same list, so it lives out here rather
// than being rebuilt on each render.
const adminNav: NavItem[] = [
  { label: 'Admin Dashboard', path: '/admin/dashboard', icon: <AdminPanelSettingsIcon /> },
  { label: 'Website content', path: '/admin/content', icon: <WebIcon /> },
  { label: 'Users', path: '/admin/users', icon: <GroupsIcon /> },
  { label: 'Instructors', path: '/admin/instructors', icon: <RecordVoiceOverIcon /> },
  { label: 'Billing', path: '/admin/billing', icon: <PaymentIcon /> },
  { label: 'Organizations', path: '/admin/organizations', icon: <BusinessIcon /> },
  { label: 'Org requests', path: '/admin/organization-requests', icon: <AddBusinessIcon /> },
  { label: 'Hosts', path: '/admin/hosts', icon: <HomeWorkIcon /> },
  { label: 'Courses', path: '/admin/courses', icon: <SchoolIcon /> },
  { label: 'Discounts', path: '/admin/discounts', icon: <LocalOfferIcon /> },
  { label: 'Events', path: '/admin/events', icon: <CalendarMonthIcon /> },
  { label: 'Invoices', path: '/admin/invoices', icon: <ReceiptIcon /> },
  { label: 'Outside payments', path: '/admin/outside-payments', icon: <PaymentsIcon /> },
  { label: 'Chat', path: '/admin/chat', icon: <ChatIcon /> },
  { label: 'Auditing', path: '/admin/auditing', icon: <GavelIcon /> },
  { label: 'Thinkific', path: '/admin/thinkific', icon: <CastForEducationIcon /> },
  { label: 'HubSpot sync', path: '/admin/hubspot', icon: <SyncAltIcon /> },
];

const externalLinks: ExternalLink[] = [
  { label: 'HubSpot', url: 'https://app.hubspot.com/', icon: 'H' },
  { label: 'Thinkific', url: 'https://www.humanriskacademy.com/manage/courses', icon: 'T' },
  { label: 'Stripe', url: 'https://dashboard.stripe.com/acct_1PP82NCvP5Udah66', icon: 'S' },
];

const linkColors: Record<string, string> = {
  HubSpot: '#ff7a59',
  Thinkific: '#4c8bf5',
  Stripe: STRIPE_BRAND,
};

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { active, platformView } = useActiveContext();
  const sb = theme.palette.sidebar;
  const [requestNewOrgOpen, setRequestNewOrgOpen] = useState(false);
  const [requestNewOrgToast, setRequestNewOrgToast] = useState<string | null>(null);

  // Pull the billing summary once for the sidebar; this is the same query
  // BillingPage uses, so navigating to /billing is instant (cache hit).
  // The endpoint already 304s for personal vs org context the same way
  // BillingPage does — we don't need a separate lightweight count endpoint.
  const { data: billing } = useBilling();
  const unpaidCount = billing?.unpaid_invoices_count ?? 0;

  const dashboardNavItem: NavItem = { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> };

  const mainNav: NavItem[] = [
    { label: 'My Courses', path: '/my-courses', icon: <SchoolIcon /> },
    // The LMS hub: every on-demand course and offer. Until this existed the only
    // way in was launching a course you already owned.
    { label: 'Course Hub', path: '/hub', icon: <CastForEducationIcon /> },
    { label: 'Upcoming Events', path: '/events', icon: <EventIcon /> },
    { label: 'Billing', path: '/billing', icon: <PaymentIcon />, badgeCount: unpaidCount },
  ];

  const teamNav: NavItem[] = [
    { label: 'My Team', path: '/organization', icon: <GroupsIcon /> },
    { label: 'Group Packages', path: '/organization/packages', icon: <InventoryIcon /> },
    { label: 'Employee Analytics', path: '/organization/analytics', icon: <BarChartIcon /> },
    { label: 'Billing', path: '/billing', icon: <PaymentIcon />, badgeCount: unpaidCount },
  ];

  // Instructor nav per admin spec 2026-06-11: "My Hosts" dropped (host info
  // now lives inline on each event's detail header), replaced with a
  // read-only copy of the system-admin All Events list so instructors can
  // see the full schedule, not just their own assignments.
  const instructorNav: NavItem[] = [
    { label: 'My Events', path: '/instructor/events', icon: <CalendarMonthIcon /> },
    { label: 'All Events', path: '/instructor/all-events', icon: <EventIcon /> },
  ];


  const inOrgContext = active.kind === 'org';
  const isPlatformAdmin = useIsSystemAdmin();
  // useIsInstructor() also returns true for system admins (so they can QA
  // the instructor surface); the sidebar should only show instructor links
  // when the user actually carries the role or has an instructor record.
  const hasInstructorRole =
    user?.roles?.includes('instructor') === true || profile?.role === 'instructor';
  const hasInstructorRecord = profile?.has_instructor_profile === true;
  const canInstruct = hasInstructorRole || hasInstructorRecord;

  // Surface "Request new organization" only for users who already belong to
  // at least one org — the backend gate matches. Hidden for system admins and
  // instructors: it's a learner/org-member action, not relevant to their roles.
  const canRequestNewOrg =
    (profile?.memberships?.length ?? 0) > 0 && !isPlatformAdmin && !canInstruct;

  // A user who is BOTH a system admin and an instructor record holder can flip
  // between the two platform views from the account dropdown — so we surface
  // only the selected one (default: admin) instead of stacking both. Everyone
  // else keeps their single section.
  const dualPlatformRole = isPlatformAdmin && canInstruct;
  const platformViewEffective: PlatformView = platformView ?? 'admin';

  // Platform-role sections (Admin, Instructor) are scoped to the personal
  // account. When the user switches into an organization context, the sidebar
  // should only surface that org's links — even if the underlying user is a
  // system admin or instructor on the platform.
  const showAdmin =
    isPlatformAdmin && !inOrgContext && (!dualPlatformRole || platformViewEffective === 'admin');
  const showInstructor =
    canInstruct && !inOrgContext && (!dualPlatformRole || platformViewEffective === 'instructor');

  // Label under the user's name in the account footer. Reflects the active
  // platform view; org membership (member/admin/owner) reads as "Personal"
  // because that's the consumer-facing identity.
  const roleLabel = showInstructor
    ? 'Instructor'
    : showAdmin
      ? 'System Admin'
      : canInstruct
        ? 'Instructor'
        : isPlatformAdmin
          ? 'System Admin'
          : 'Personal';

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/admin/dashboard') return location.pathname === '/admin/dashboard';
    // Exact match (not startsWith) so /organization doesn't also light up while
    // on /organization/packages.
    if (path === '/organization') return location.pathname === '/organization';
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item: NavItem) => (
    <ListItemButton
      key={item.path}
      component={RouterLink}
      to={item.path}
      onClick={onMobileClose}
      sx={{
        mx: 1,
        borderRadius: 1,
        mb: 0.5,
        color: isActive(item.path) ? sb.activeText : sb.text,
        backgroundColor: isActive(item.path) ? sb.activeBackground : 'transparent',
        '&:hover': {
          backgroundColor: isActive(item.path) ? sb.activeBackground : sb.hoverBackground,
        },
      }}
    >
      <ListItemIcon sx={{ color: isActive(item.path) ? sb.activeText : sb.text, minWidth: 36 }}>
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        sx={{
          '& .MuiListItemText-primary': {
            fontSize: '14px',
            fontWeight: isActive(item.path) ? 600 : 500,
            color: isActive(item.path) ? sb.activeText : sb.text,
          },
        }}
      />
      {/* Pill counter for items with a non-zero badgeCount. MUI's <Badge>
          would float over the icon, which loses the at-a-glance pairing
          with the label — so we render a custom inline pill on the right
          edge instead. Hidden entirely when count is 0 to keep the nav
          calm for users who have nothing owed. */}
      {item.badgeCount && item.badgeCount > 0 ? (
        <Badge
          badgeContent={item.badgeCount}
          color="error"
          sx={{ mr: 1.5, '& .MuiBadge-badge': { position: 'static', transform: 'none', fontSize: 10, height: 18, minWidth: 18 } }}
        />
      ) : null}
    </ListItemButton>
  );

  const sectionLabel = (text: string) => (
    <Typography
      variant="overline"
      sx={{ px: 2, pt: 2, pb: 0.5, display: 'block', fontSize: '11px', fontWeight: 600, color: sb.textSecondary, letterSpacing: '0.1em' }}
    >
      {text}
    </Typography>
  );

  const drawerContent = (
    <>
      <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="img"
          src="/wz-icon-white.png"
          alt="Wicklander-Zulawski"
          sx={{ height: 36, width: 36, objectFit: 'contain', flexShrink: 0 }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, minWidth: 0 }}>
          <Typography
            sx={{
              color: '#fff',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Human Risk Academy
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: '9.5px',
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              mt: '2px',
            }}
          >
            Interview Anyone
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 1 }} />

      <List sx={{ flex: 1, pt: 1 }}>
        {/* Hide learner nav (Dashboard / My Courses / On-Demand Courses /
            Upcoming Events / Billing) for system admins — their landing
            page is /admin/dashboard and they don't need the learner views
            cluttering the sidebar. */}
        {/* Instructors get a focused sidebar (Dashboard + Instructor section
            only) — the learner links (My Courses / Upcoming Events /
            Billing) are hidden for them per admin spec 2026-06-11, same as
            they are for system admins. */}
        {!showAdmin && renderNavItem(dashboardNavItem)}

        {!showAdmin && !showInstructor && !inOrgContext && mainNav.map(renderNavItem)}

        {inOrgContext && (
          <>
            {sectionLabel(active.name)}
            {teamNav.map(renderNavItem)}
          </>
        )}

        {showAdmin && (
          <>
            {sectionLabel('Admin')}
            {adminNav.map(renderNavItem)}
          </>
        )}
        {showInstructor && (
          <>
            {sectionLabel('Instructor')}
            {instructorNav.map(renderNavItem)}
          </>
        )}
      </List>

      {showAdmin && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 1 }} />
          <Box sx={{ px: 1, py: 1.5 }}>
            <Typography
              variant="overline"
              sx={{ px: 1, pb: 0.5, display: 'block', fontSize: '10px', fontWeight: 600, color: sb.textSecondary, letterSpacing: '0.1em' }}
            >
              External
            </Typography>
            {externalLinks.map((link) => (
              <ListItemButton
                key={link.label}
                component="a"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onMobileClose}
                sx={{
                  borderRadius: 1,
                  mb: 0.25,
                  py: 0.75,
                  '&:hover': { backgroundColor: sb.hoverBackground },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '5px',
                      backgroundColor: linkColors[link.label],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 700,
                    }}
                  >
                    {link.icon}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={link.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '13px',
                      fontWeight: 500,
                      color: sb.text,
                    },
                  }}
                />
                <OpenInNewIcon sx={{ fontSize: 13, color: sb.textSecondary, ml: 0.5 }} />
              </ListItemButton>
            ))}
          </Box>
        </>
      )}

      {/* Footer block: marketing-site link + account chip. Sharing a single
          bordered container (instead of two free-floating rows above the
          drawer's natural background) keeps the bottom of the sidebar
          visually anchored regardless of whether the External block above
          is rendered. The marketing link is visible to every signed-in user
          regardless of role or active org; same-tab navigation matches the
          "back to" framing (we're leaving the app, not opening a sibling
          tool the way HubSpot/Thinkific/Stripe do above). */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', px: 1, py: 1 }}>
        {canRequestNewOrg && (
          <ListItemButton
            onClick={() => { setRequestNewOrgOpen(true); onMobileClose?.(); }}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              py: 0.75,
              color: sb.text,
              '&:hover': { backgroundColor: sb.hoverBackground },
            }}
          >
            <ListItemIcon sx={{ color: sb.text, minWidth: 32 }}>
              <AddBusinessIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary="Request new organization"
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: '13px',
                  fontWeight: 500,
                  color: sb.text,
                },
              }}
            />
          </ListItemButton>
        )}

        <ListItemButton
          component="a"
          href="https://w-z.com"
          onClick={onMobileClose}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            py: 0.75,
            color: sb.text,
            '&:hover': { backgroundColor: sb.hoverBackground },
          }}
        >
          <ListItemIcon sx={{ color: sb.text, minWidth: 32 }}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary="Back to WZ"
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '13px',
                fontWeight: 500,
                color: sb.text,
              },
            }}
          />
        </ListItemButton>

        <Box sx={{ pt: 0.5, px: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountSwitcher placement="top-start" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: sb.text }} noWrap>
              {displayNameFor(profile, user)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: sb.textSecondary }} noWrap>
              {roleLabel}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );

  const paperSx = {
    width: DRAWER_WIDTH,
    backgroundColor: sb.background,
    borderRight: 'none',
    top: 0,
    height: '100%',
  };

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': paperSx,
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': paperSx,
        }}
      >
        {drawerContent}
      </Drawer>

      {requestNewOrgOpen && (
        <RequestNewOrgDialog
          open
          onClose={() => setRequestNewOrgOpen(false)}
          onSubmitted={setRequestNewOrgToast}
        />
      )}
      <Snackbar open={!!requestNewOrgToast} autoHideDuration={5000} onClose={() => setRequestNewOrgToast(null)}>
        <Alert severity="success" onClose={() => setRequestNewOrgToast(null)}>{requestNewOrgToast}</Alert>
      </Snackbar>
    </>
  );
}

export { DRAWER_WIDTH };
